import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import ReplyButton from './icon/replybutton';
import DeleteButtons from './icon/deleteicon';
import ReactionIcon from './icon/ReactionIcon';
import ReactionButton from './shared/ReactionButton';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getLikerDisplayName, getAuthorDisplayName, formatAuthorMetaLine } from '../utils/displayName';
import { getMediaMimeType, isMediaImage, isMediaVideo } from '../utils/mediaDetect';
import PostMediaGrid from './shared/PostMediaGrid';
import AuthorNameLink from './shared/AuthorNameLink';
import ArticleCard, { BLOG_CARD_PROPS } from './shared/ArticleCard';
import HiringCard from './shared/HiringCard';
import LookingForJobCard from './shared/LookingForJobCard';
import { getTopReactions, DEFAULT_REACTION } from '../utils/reactions';
import { htmlToExcerpt } from '../utils/sanitizeHtml';
import { getApiBaseUrl } from '../config/api';
import { linkifyMentions, escapeUserText } from '../utils/mentions';
import useLiveRefresh from '../hooks/useLiveRefresh';
import './PostlistGuest.css';

const TYPE_TABS = [
  { id: 'discussion', label: 'Discussion' },
  { id: 'article', label: 'Articles' },
  { id: 'blog', label: 'Blogs' },
];
const GUEST_PAGE_SIZE = 3;

const getEffectivePostType = (post) => {
  if (post?.post_type === 'repost' && post?.original_post) {
    return post.original_post.post_type || 'text';
  }
  return post?.post_type || 'text';
};

const isArticlePost = (post) => getEffectivePostType(post) === 'article';

const PostListGuest = ({ onReply, user, onRefresh }) => {
 const [replyContents, setReplyContents] = useState({});
  const [mediaFiles, setMediaFiles] = useState({});
  const [showReplyBox, setShowReplyBox] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pagePosts, setPagePosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [discussionCount, setDiscussionCount] = useState(0);
  const [articleCount, setArticleCount] = useState(0);
  const [blogCount, setBlogCount] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [likes, setLikes] = useState({});
  const [userLiked, setUserLiked] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const scrollRef = useRef(null);
  const feedTopRef = useRef(null);
  const fetchIdRef = useRef(0);
  const shouldScrollToFeedRef = useRef(false);
    const [likedPosts, setLikedPosts] = useState([]); // ✅ Declare here
  const [expandedContent, setExpandedContent] = useState({});
const [showDropdown, setShowDropdown] = useState({});
const [showAllImages, setShowAllImages] = useState({}); // key by reply.id
  const [popupPhoto, setPopupPhoto] = useState(null);
  const closePopup = () => setPopupPhoto(null);

const [replies, setReplies] = useState({});
const [searchTerm, setSearchTerm] = useState('ALL');




  const [editPost, setEditPost] = useState(null);
const [showEditModal, setShowEditModal] = useState(false);
  const [visibleReplies, setVisibleReplies] = useState({});


  const [isDragging, setIsDragging] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
const [searchQuery, setSearchQuery] = useState('');
 const [selectedCategory, setSelectedCategory] = useState('');
  const [typeFilter, setTypeFilter] = useState('discussion');
  const [showPostForm, setShowPostForm] = useState(false);
  const API_URL = getApiBaseUrl();
const [nestedReplyMedia, setNestedReplyMedia] = useState({});
const [showNestedReplies, setShowNestedReplies] = useState({});
const [activeReplyBox, setActiveReplyBox] = useState(null);
const [nestedReplyText, setNestedReplyText] = useState({});
const [whoLiked, setWhoLiked] = useState({}); // Store who liked each post/reply
const [showLikesModal, setShowLikesModal] = useState(null); // Track which modal to show
const [hoveredLikePosition, setHoveredLikePosition] = useState(null); // Track hover position

useEffect(() => {
  const fetchLikes = async () => {
    try {
      const res = await fetch(`${API_URL}/get_likes.php`);
      const data = await res.json();
      if (data.success) {
        setLikes(data.total_likes || {});
        setUserLiked(data.user_liked || {});
        setUserReactions(data.user_reactions || {});
        setWhoLiked(data.who_liked || {}); // Also fetch who liked for guests
      }
    } catch (err) {
      console.error('Error fetching likes:', err);
    }
  };
  fetchLikes();
}, [API_URL]);

 useEffect(() => {
  const fetchLikes = async () => {
    if (!user?.id) return; // Skip if user is not loaded yet

    try {
      const res = await fetch(`${API_URL}/get_likes.php?user_id=${user.id}`);
      const data = await res.json();

      if (data.success) {
        // ✅ Set total like counts (flat structure: post_123, reply_456, etc.)
        setLikes(data.total_likes || {});

        // ✅ Store what the current user has liked (post, reply, or nested reply)
        setUserLiked(data.user_liked || {});
        setUserReactions(data.user_reactions || {});
        
        // ✅ Store who liked each post/reply
        setWhoLiked(data.who_liked || {});
      } else {
        console.warn("Failed to load like data:", data.message);
      }
    } catch (err) {
      console.error("Error fetching likes:", err.message);
    }
  };

  fetchLikes();
}, [API_URL, user?.id, pagePosts]);

  // Toggle like/unlike
  const handleLikeToggle = async (postId) => {
    const isLiked = likedPosts.includes(postId);

    // Optimistic UI update
    setLikes((prev) => ({
      ...prev,
      [postId]: (prev[postId] || 0) + (isLiked ? -1 : 1),
    }));

    const updatedLikedPosts = isLiked
      ? likedPosts.filter((id) => id !== postId)
      : [...likedPosts, postId];

    setLikedPosts(updatedLikedPosts);
    localStorage.setItem('likedPosts', JSON.stringify(updatedLikedPosts));

    try {
      await fetch(`${API_URL}/likeguest.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: postId, action: isLiked ? 'unlike' : 'like' }),
      });
    } catch (err) {
      console.error('Error toggling like:', err.message);
    }
  };
const getSharePostId = (post) => {
  if (post?.post_type === 'repost' && post?.original_post?.id) {
    return post.original_post.id;
  }
  return post?.id;
};

const handleShare = (id) => {
  const shareUrl = `${window.location.origin}/home/public/${id}`;

  if (navigator.share) {
    navigator
      .share({
        title: 'Check out this post!',
        url: shareUrl,
      })
      .then(() => console.log('Post shared successfully'))
      .catch((err) => console.error('Error sharing', err));
  } else {
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  }
};


  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadGuestPage = useCallback(async (page, options = {}) => {
    if (!API_URL) {
      setPagePosts([]);
      setTotalCount(0);
      setHasMorePages(false);
      setPageLoading(false);
      return;
    }

    const silent = options.silent === true;
    const safePage = Math.max(1, page);
    const requestId = silent ? fetchIdRef.current : ++fetchIdRef.current;
    if (!silent) setPageLoading(true);

    try {
      const params = new URLSearchParams({
        admin_only: '1',
        guest_type:
          typeFilter === 'article' ? 'article' : typeFilter === 'blog' ? 'blog' : 'discussion',
        limit: String(GUEST_PAGE_SIZE),
        offset: String((safePage - 1) * GUEST_PAGE_SIZE),
      });
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (selectedCategory) params.set('category', selectedCategory);

      const res = await fetch(`${API_URL}/get_posts.php?${params.toString()}`);
      if (!res.ok) {
        throw new Error(`GET ${API_URL}/get_posts.php failed with ${res.status}`);
      }
      const data = await res.json();
      if (requestId !== fetchIdRef.current) return;

      if (data.success && Array.isArray(data.posts)) {
        setPagePosts(data.posts);
        setCurrentPage(safePage);
        setTotalCount(typeof data.total === 'number' ? data.total : data.posts.length);
        setHasMorePages(Boolean(data.has_more));
        if (typeof data.discussion_total === 'number') {
          setDiscussionCount(data.discussion_total);
        }
        if (typeof data.article_total === 'number') {
          setArticleCount(data.article_total);
        }
        if (typeof data.blog_total === 'number') {
          setBlogCount(data.blog_total);
        }
        if (Array.isArray(data.categories)) {
          setCategoryOptions(data.categories);
        }
      } else if (!silent) {
        setPagePosts([]);
        setTotalCount(0);
        setHasMorePages(false);
      }
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      console.error('Error fetching guest posts:', err);
      if (!silent) {
        setPagePosts([]);
        setTotalCount(0);
        setHasMorePages(false);
      }
    } finally {
      if (requestId === fetchIdRef.current && !silent) {
        setPageLoading(false);
      }
    }
  }, [API_URL, typeFilter, debouncedSearch, selectedCategory]);

  useEffect(() => {
    loadGuestPage(1);
  }, [loadGuestPage]);

  const guestPageRef = useRef(currentPage);
  guestPageRef.current = currentPage;
  useLiveRefresh(
    () => loadGuestPage(guestPageRef.current, { silent: true }),
    12000,
    true
  );

  useEffect(() => {
    if (pageLoading || !shouldScrollToFeedRef.current) return;
    shouldScrollToFeedRef.current = false;
    const el = feedTopRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const header = document.querySelector('.header-container');
      const headerHeight = header?.parentElement?.offsetHeight || header?.offsetHeight || 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }, [pageLoading, currentPage, pagePosts]);

  const currentPosts = pagePosts;
  const totalPages = Math.ceil(totalCount / GUEST_PAGE_SIZE);

const formatDate = (timestamp, fixedTimeZone = null) => {
  // Decide which timezone to use: user’s local or fixed
  const timeZone = fixedTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Convert timestamp from UTC to chosen time zone
  const localDate = new Date(
    new Date(timestamp).toLocaleString("en-US", { timeZone })
  );

  const localNow = new Date(
    new Date().toLocaleString("en-US", { timeZone })
  );

  const diff = (localNow.getTime() - localDate.getTime()) / 1000;

  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;

  // Format date in chosen time zone
  return new Intl.DateTimeFormat("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone
  }).format(localDate);
};

// Example usage:
// Auto-adjust to user’s local time
console.log(formatDate("2025-08-13T06:30:00Z"));

// Force Singapore time
console.log(formatDate("2025-08-13T06:30:00Z", "Asia/Singapore"));


  const handlePageChange = (newPage) => {
    if (pageLoading) return;
    if (newPage < 1) return;
    if (newPage > totalPages && !hasMorePages) return;
    shouldScrollToFeedRef.current = true;
    loadGuestPage(newPage);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-trigger')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



const handleLike = async (targetId, type, reactionType = DEFAULT_REACTION) => {
  const key = type === 'post' ? `post_${targetId}` :
              type === 'reply' ? `reply_${targetId}` :
              `child_reply_${targetId}`;

  const liked = !!userLiked[key];
  const currentReaction = userReactions[key] || DEFAULT_REACTION;
  const removing = reactionType === null || (liked && reactionType === currentReaction);
  const changing = liked && !removing && reactionType !== currentReaction;
  const nextReaction = removing ? null : (reactionType || DEFAULT_REACTION);

  // Optimistic update
  setLikes((prev) => ({
    ...prev,
    [key]: removing
      ? Math.max(0, (prev[key] || 1) - 1)
      : changing
        ? (prev[key] || 1)
        : (prev[key] || 0) + 1,
  }));

  setUserLiked((prev) => ({
    ...prev,
    [key]: !removing,
  }));

  setUserReactions((prev) => {
    const next = { ...prev };
    if (removing) delete next[key];
    else next[key] = nextReaction;
    return next;
  });

  setWhoLiked((prev) => {
    const currentList = Array.isArray(prev[key]) ? prev[key] : [];
    if (removing) {
      return {
        ...prev,
        [key]: currentList.filter((entry) => {
          if (user?.id) return entry.user_id !== user.id;
          return entry.username !== 'Guest';
        }),
      };
    }
    const entry = {
      user_id: user?.id || null,
      username: user?.username || 'Guest',
      first_name: user?.first_name || user?.firstname || '',
      last_name: user?.last_name || user?.lastname || '',
      profile_pic: user?.profile_pic,
      reaction_type: nextReaction,
    };
    const idx = currentList.findIndex((e) =>
      user?.id ? e.user_id === user.id : e.username === 'Guest' && !e.user_id
    );
    if (idx >= 0) {
      const updated = [...currentList];
      updated[idx] = { ...updated[idx], reaction_type: nextReaction };
      return { ...prev, [key]: updated };
    }
    return { ...prev, [key]: [...currentList, entry] };
  });

  try {
    const res = await fetch(`${API_URL}/likeguest.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_id: targetId,
        target_type: type,
        action: removing ? 'unlike' : 'like',
        reaction_type: nextReaction || DEFAULT_REACTION,
        ...(user?.id ? { user_id: user.id } : {}),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (data.success && typeof data.like_count === 'number') {
      setLikes((prev) => ({ ...prev, [key]: data.like_count }));
    }
    if (data.success && data.reaction_type && !removing) {
      setUserReactions((prev) => ({ ...prev, [key]: data.reaction_type }));
    }
  } catch (err) {
    console.error('Like/unlike failed:', err);
  }
};


const isImage = (url, type) => isMediaImage(url, type);
const isVideo = (url, type) => isMediaVideo(url, type);
const isAudio = (url) => /\.(mp3|wav|ogg)$/i.test(url);
const isDocument = (url) => /\.(pdf|docx?|pptx?|xlsx?)$/i.test(url);

const getMimeType = (url, type) => {
  if (isVideo(url, type) || isImage(url, type)) return getMediaMimeType(url, type);
  if (isAudio(url)) return 'audio/mpeg';
  if (isDocument(url)) return 'application/pdf';
  return 'application/octet-stream';
};

const handleNestedReplySubmit = async (postId, parentReplyId) => {
  if (!user?.id) {
    alert('You must be logged in to reply.');
    return;
  }

  const text = nestedReplyText[parentReplyId]?.trim();
  const files = nestedReplyMedia[parentReplyId] || [];

  if (!text && files.length === 0) {
    alert('Please write a reply or attach media.');
    return;
  }

  const formData = new FormData();
  formData.append("post_id", postId);
  formData.append("user_id", user.id);
  formData.append("text", text || "");
  formData.append("parent_reply_id", parentReplyId);

  // Append files to FormData
  files.forEach((file, idx) => {
    formData.append("media[]", file);
  });

  try {
    const res = await fetch(`${API_URL}/add_reply.php`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();
    if (data.success) {
      // Refresh posts to show the new nested reply
      if (onRefresh) {
        onRefresh();
      }
      // Clear input and files
      setNestedReplyText((prev) => ({ ...prev, [parentReplyId]: "" }));
      setNestedReplyMedia((prev) => ({ ...prev, [parentReplyId]: [] }));
      setActiveReplyBox(null);
    } else {
      alert(data.message || "Failed to post reply");
    }
  } catch (err) {
    console.error("Error posting nested reply:", err);
    alert("Error posting reply. Please try again.");
  }
};

const createLinkifiedText = (text) => {
  if (!text) return "";

  const withMentions = linkifyMentions(escapeUserText(text));

  // ✅ Step 1: Fix broken URLs caused by line breaks
  const normalizedText = withMentions.replace(
    /(https?:\/\/[^\s]+)\s*\n\s*([^\s]+)/g,
    "$1$2"
  );

  // ✅ Step 2: Detect URLs + emails
  const regex =
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|((https?:\/\/|www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/[^\s]*)?)/gi;

  return normalizedText.replace(regex, (match) => {
    // 📧 Email
    if (match.includes("@")) {
      return `<a href="mailto:${match}" style="color:#0077b6; text-decoration:none;">
        ${match}
      </a>`;
    }

    // 🌐 URL
    const href = match.startsWith("http")
      ? match
      : `https://${match}`;

    return `<a href="${href}" target="_blank" rel="noopener noreferrer"
      style="color:#0077b6; text-decoration:none;">
      ${match}
    </a>`;
  }).replace(
    /(^|\s)#([a-zA-Z0-9_]+)/g,
    '$1<a href="/hashtag/$2" style="color:#2563eb;text-decoration:none;">#$2</a>'
  );
};



  return (
<div className="post-list pg-feed" ref={feedTopRef}>

  
<div className="pg-header">
  <h2 className="pg-title">
    Latest
    <span className="pg-title-accent">
      {typeFilter === 'article' ? ' Articles' : typeFilter === 'blog' ? ' Blogs' : ' Discussion'}
    </span>
  </h2>

  <div className="pg-controls">
    <label className="pg-search">
      <span className="pg-search-icon" aria-hidden="true">
        <Search size={18} strokeWidth={2} />
      </span>
      <input
        type="text"
        className="pg-search-input"
        placeholder={
          typeFilter === 'article'
            ? 'Search articles...'
            : typeFilter === 'blog'
              ? 'Search blogs...'
              : 'Search discussion...'
        }
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        aria-label={
          typeFilter === 'article'
            ? 'Search articles'
            : typeFilter === 'blog'
              ? 'Search blogs'
              : 'Search discussion'
        }
      />
    </label>

    <select
      className="pg-category"
      value={selectedCategory}
      onChange={(e) => setSelectedCategory(e.target.value)}
      aria-label="Filter by category"
    >
      <option value="">All Categories</option>
      {[...categoryOptions].map((category) => (
        <option key={category} value={category}>
          {category}
        </option>
      ))}
    </select>
  </div>
</div>

<div className="pg-tabs" role="tablist" aria-label="Filter guest posts">
  {TYPE_TABS.map((tab) => {
    const count =
      tab.id === 'article' ? articleCount : tab.id === 'blog' ? blogCount : discussionCount;
    return (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={typeFilter === tab.id}
        className={`pg-tab${typeFilter === tab.id ? ' is-active' : ''}`}
        onClick={() => {
          if (typeFilter === tab.id) return;
          setTypeFilter(tab.id);
          setSelectedCategory('');
          setCurrentPage(1);
        }}
      >
        {tab.label}
        <span className="pg-tab-count">{count}</span>
      </button>
    );
  })}
</div>

      {pageLoading && (
        <p className="pg-feed-loading">Loading posts…</p>
      )}

      {!pageLoading && currentPosts.length === 0 && (
        <p className="pg-empty">
          {typeFilter === 'article'
            ? 'No articles to display yet.'
            : typeFilter === 'blog'
              ? 'No blogs to display yet.'
              : 'No admin discussion posts to display yet.'}
        </p>
      )}

      {!pageLoading && currentPosts.map((post) => (

      
      <div key={post.id} className="post-card pg-card">

        <div className="pg-author">
          <img
            src={resolveAvatarUrl(post, API_URL)}
            alt="avatar"
            onError={handleAvatarError}
            className="pg-avatar"
          />
          <div>
            <div className="pg-author-name">
              <AuthorNameLink user={post} style={{ fontWeight: 700, color: '#0f172a' }} />
              {post.post_type === 'repost' && (
                <span style={{ fontWeight: 500, color: '#65676b', marginLeft: 6 }}>
                  reposted
                </span>
              )}
            </div>
            <div className="pg-author-meta">{formatAuthorMetaLine(post, formatDate)}</div>
          </div>
        </div>

        {getEffectivePostType(post) === 'article' && post.post_type !== 'repost' ? (
          <ArticleCard post={post} linkPrefix="/home/public" />
        ) : getEffectivePostType(post) === 'blog' && post.post_type !== 'repost' ? (
          <ArticleCard post={post} linkPrefix="/home/public" {...BLOG_CARD_PROPS} />
        ) : post.post_type === 'hiring' ? (
          <HiringCard post={post} linkPrefix="/home/public" />
        ) : post.post_type === 'looking_for_job' ? (
          <LookingForJobCard post={post} linkPrefix="/home/public" />
        ) : post.post_type === 'repost' ? (
          <>
            {!!String(post.content || post.repost_caption || '').trim() &&
              String(post.content || post.repost_caption || '').trim() !== 'Repost' && (
              <p
                style={{
                  fontSize: '15px',
                  color: '#374151',
                  marginBottom: '12px',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.55,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: createLinkifiedText(String(post.content || post.repost_caption || '')),
                }}
              />
            )}
            {post.original_post && (() => {
              const original = post.original_post;
              const originalType = original.post_type || 'text';
              const discussionPath = `/home/public/${original.id}`;
              const excerptSource =
                original.hiring_description ||
                original.looking_for_job_description ||
                original.text ||
                original.content ||
                '';
              const excerpt = htmlToExcerpt(excerptSource, 160);

              return (
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 14,
                    background: '#fafafa',
                    marginBottom: 8,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <img
                      src={resolveAvatarUrl(original, API_URL)}
                      alt=""
                      onError={handleAvatarError}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        background: '#e5e7eb',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#111827',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {getAuthorDisplayName(original)}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6b7280',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {formatAuthorMetaLine(original, formatDate)}
                      </div>
                    </div>
                  </div>

                  {originalType === 'hiring' && (
                    <div
                      style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: '#0f766e',
                        background: '#ccfbf1',
                        padding: '3px 8px',
                        borderRadius: 4,
                        marginBottom: 8,
                      }}
                    >
                      Hiring
                    </div>
                  )}

                  {originalType === 'looking_for_job' && (
                    <div
                      style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: '#1d4ed8',
                        background: '#dbeafe',
                        padding: '3px 8px',
                        borderRadius: 4,
                        marginBottom: 8,
                      }}
                    >
                      Looking for job
                    </div>
                  )}

                  {original.title && original.title !== 'Repost' && (
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: '#102a43',
                        marginBottom: 6,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {original.title}
                    </div>
                  )}

                  {(original.hiring_company || original.hiring_location) && (
                    <div
                      style={{
                        fontSize: 13,
                        color: '#475569',
                        marginBottom: 6,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {[original.hiring_company, original.hiring_location]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}

                  {excerpt && (
                    <div
                      style={{
                        fontSize: 14,
                        color: '#374151',
                        lineHeight: 1.5,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {excerpt}
                    </div>
                  )}

                  {Array.isArray(original.media) && original.media[0] && (
                    <div
                      style={{
                        marginTop: 10,
                        borderRadius: 10,
                        overflow: 'hidden',
                        maxWidth: '100%',
                      }}
                    >
                      {isImage(original.media[0].url, original.media[0].type) ? (
                        <img
                          src={original.media[0].url}
                          alt=""
                          style={{
                            width: '100%',
                            maxHeight: 280,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : isVideo(original.media[0].url, original.media[0].type) ? (
                        <video
                          controls
                          playsInline
                          preload="metadata"
                          style={{
                            width: '100%',
                            maxHeight: 280,
                            background: '#000',
                            display: 'block',
                          }}
                        >
                          <source
                            src={original.media[0].url}
                            type={getMimeType(original.media[0].url, original.media[0].type)}
                          />
                        </video>
                      ) : null}
                    </div>
                  )}

                  <Link
                    to={discussionPath}
                    style={{
                      display: 'inline-block',
                      marginTop: 12,
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#0a66c2',
                      textDecoration: 'none',
                    }}
                  >
                    View discussion →
                  </Link>
                </div>
              );
            })()}
          </>
        ) : (
        <>
                          <h2 className="pg-post-title">{post.title}</h2>

        <p
        className="pg-post-body"
        dangerouslySetInnerHTML={{
          __html:
            expandedContent[post.id] || post.content.split(" ").length <= 50
              ? createLinkifiedText(String(post.content))
              : createLinkifiedText(
                  String(post.content).split(" ").slice(0, 50).join(" ") + "..."
                ),
        }}
      ></p>
      
      {post.content.split(" ").length > 50 && (
        <button
          type="button"
          className="pg-see-more"
          onClick={() =>
            setExpandedContent((prev) => ({
              ...prev,
              [post.id]: expandedContent[post.id] ? null : post.content,
            }))
          }
        >
          {expandedContent[post.id] ? "See less" : "See more"}
        </button>
      )}

             {Array.isArray(post.media) && post.media.length > 0 && (
        <PostMediaGrid
          media={post.media}
          onImageClick={setPopupPhoto}
        />
      )}
        </>
        )}
       
             {/* Popup */}
             {popupPhoto && (
               <div
                 onClick={closePopup}
                 style={{
                   position: 'fixed',
                   top: 0,
                   left: 0,
                   width: '100vw',
                   height: '100vh',
                   backgroundColor: 'rgba(0,0,0,0.8)',
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   zIndex: 1000,
                   cursor: 'pointer',
                 }}
               >
                 <img
                   src={popupPhoto}
                   alt="popup"
                   style={{
                     maxWidth: '90%',
                     maxHeight: '90%',
                     borderRadius: '10px',
                     boxShadow: '0 0 20px rgba(0,0,0,0.5)',
                   }}
                   onClick={(e) => e.stopPropagation()}
                 />
               </div>
             )}
    

      {/* Facebook-style Reactions and Actions Section (match PostList, no Reply button) */}
      {(() => {
        const postKey = `post_${post.id}`;
        const likeCount = likes[postKey] || 0;
        const likedUsers = whoLiked[postKey] || [];

        // Names text, e.g. "Cathrine Biñas Canlas, Ann Jacobe and 13 others"
        const formatReactionNames = () => {
          if (likedUsers.length === 0) return null;
          if (likedUsers.length === 1) return getLikerDisplayName(likedUsers[0]);
          if (likedUsers.length === 2) {
            return `${getLikerDisplayName(likedUsers[0])} and ${getLikerDisplayName(likedUsers[1])}`;
          }
          const names = likedUsers.slice(0, 2).map((u) => getLikerDisplayName(u)).join(', ');
          return `${names} and ${likedUsers.length - 2} others`;
        };

        const reactionText = formatReactionNames();
        const topReactions = getTopReactions(likedUsers, 3);

        // Count only top-level replies (same as PostList)
        const mainReplies = (post.replies || []).filter(
          (reply) => !reply.parent_reply_id || reply.parent_reply_id === 0
        );
        const replyCount = mainReplies.length;

        return (
          <div className="pg-actions">
            {/* Top: reaction icons + names */}
            {likeCount > 0 && (
              <div className="pg-reactions-summary">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {topReactions.map((type, idx) => (
                    <span
                      key={type}
                      style={{
                        marginLeft: idx === 0 ? 0 : -4,
                        zIndex: topReactions.length - idx,
                        background: '#fff',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 1px #fff',
                      }}
                    >
                      <ReactionIcon type={type} size={12} />
                    </span>
                  ))}
                </div>
                {reactionText && (
                  <span
                    className="pg-reactions-text"
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      const key = postKey;
                      if (likedUsers && likedUsers.length > 0) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredLikePosition({
                          top: rect.top - 10,
                          left: rect.left,
                          key,
                        });
                        setShowLikesModal(key);
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      setShowLikesModal(null);
                      setHoveredLikePosition(null);
                    }}
                    title="Hover to see who liked this post"
                  >
                    {reactionText}
                  </span>
                )}
              </div>
            )}

            <div className="pg-actions-divider" />

            <div className="pg-action-row post-action-bar">
              <ReactionButton
                pill
                showLabel
                userReaction={userReactions[postKey] || null}
                onReact={(reactionType) => handleLike(post.id, 'post', reactionType)}
                iconSize={16}
              />

              <button
                type="button"
                className="pg-share-pill"
                aria-label="Share"
                title="Share"
                onClick={() => handleShare(getSharePostId(post))}
              >
                <FontAwesomeIcon icon={faPaperPlane} aria-hidden />
                <span className="post-action-text">Share</span>
              </button>
            </div>

            {replyCount > 0 && (
              <button
                type="button"
                className="pg-reply-link"
                onClick={() =>
                  setShowReplies((prev) => ({
                    ...prev,
                    [post.id]: !prev[post.id],
                  }))
                }
              >
                {showReplies[post.id]
                  ? 'Hide replies'
                  : `View ${replyCount} repl${replyCount === 1 ? 'y' : 'ies'} >`}
              </button>
            )}
          </div>
        );
      })()}

      {showReplies[post.id] && (
  <div style={{ marginTop: "20px" }}>
    <AnimatePresence>
      {(post.replies || [])
        .filter((r) => !r.parent_reply_id || r.parent_reply_id === 0 || r.parent_reply_id === null) // Only show top-level replies
        .slice(0, visibleReplies[post.id] ?? 7)
        .map((reply, i) => {
          // Helper function to get all child replies recursively
          const getChildReplies = (parentId, allReplies) => {
            return allReplies.filter((r) => r.parent_reply_id === parentId);
          };
          
          // Recursive component to render nested replies at any depth
          const renderNestedReply = (replyItem, depth = 0) => {
            const childReplies = getChildReplies(replyItem.id, post.replies || []);
            const hasChildren = childReplies.length > 0;
            const isExpanded = showNestedReplies[replyItem.id];
            
            return (
              <div key={replyItem.id}>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginLeft: depth > 0 ? `${40 + (depth * 20)}px` : "0px",
                    marginTop: depth > 0 ? "8px" : "0px",
                    borderLeft: depth > 0 ? "2px solid #eee" : "none",
                    paddingLeft: depth > 0 ? "10px" : "0px",
                  }}
                >
                  {/* Avatar */}
                  <img
                    src={resolveAvatarUrl(replyItem, API_URL)}
                    onError={handleAvatarError}
                    alt="avatar"
                    style={{ 
                      width: depth > 0 ? "30px" : "35px", 
                      height: depth > 0 ? "30px" : "35px", 
                      borderRadius: "50%" 
                    }}
                  />

                  {/* Reply content */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <strong style={{
                        fontSize: depth > 0 ? "12px" : "14px",
                        color: "#333030ff",
                        marginLeft: depth > 0 ? "1px" : "0px",
                      }}>
                        <AuthorNameLink user={replyItem} />
                      </strong>
                      {replyItem.created_at && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#888",
                            marginLeft: "4px",
                          }}
                        >
                          {formatDate(replyItem.created_at)}
                        </span>
                      )}
                    </div>

                    <div>
                      <p
                        style={{
                          fontSize: depth > 0 ? "14px" : "15px",
                          color: "#374151",
                          marginBottom: "8px",
                          whiteSpace: "pre-wrap",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: createLinkifiedText(
                            expandedContent[replyItem.id]
                              ? String(replyItem.text)
                              : String(replyItem.text)
                                  .split(" ")
                                  .slice(0, 50)
                                  .join(" ") +
                                (replyItem.text.split(" ").length > 50 ? "..." : "")
                          ),
                        }}
                      ></p>

                      {replyItem.text.split(" ").length > 50 && (
                        <button
                          onClick={() =>
                            setExpandedContent((prev) => ({
                              ...prev,
                              [replyItem.id]: !prev[replyItem.id],
                            }))
                          }
                          style={{
                            fontSize: "13px",
                            color: "#0077b6",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {expandedContent[replyItem.id] ? "See less" : "See more"}
                        </button>
                      )}
                    </div>

                    {/* Media */}
                    {Array.isArray(replyItem.media) && replyItem.media.length > 0 && (
                      <>
                        {replyItem.media.map((item, idx) => {
                          const isImg = isImage(item.url);
                          const isVid = isVideo(item.url);
                          return isImg ? (
                            <img
                              key={idx}
                              src={item.url}
                              alt="reply media"
                              style={{
                                margin: "5px 0",
                                width: "300px",
                                borderRadius: "4px",
                                objectFit: "contain",
                              }}
                            />
                          ) : isVid ? (
                            <video
                              key={idx}
                              controls
                              playsInline
                              style={{
                                margin: "5px 0",
                                width: "300px",
                                borderRadius: "4px",
                                objectFit: "contain",
                              }}
                            >
                              <source src={item.url} type={getMimeType(item.url)} />
                            </video>
                          ) : null;
                        })}
                      </>
                    )}

                    {/* Actions (Like / Delete / Reply) */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                      <ReactionButton
                        compact
                        iconSize={14}
                        showLabel={false}
                        userReaction={userReactions[`reply_${replyItem.id}`] || null}
                        count={likes[`reply_${replyItem.id}`] || 0}
                        onReact={(reactionType) => handleLike(replyItem.id, 'reply', reactionType)}
                        onCountClick={(e) => {
                          const key = `reply_${replyItem.id}`;
                          if (whoLiked[key] && whoLiked[key].length > 0) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoveredLikePosition({
                              top: rect.top - 10,
                              left: rect.left,
                              key,
                            });
                            setShowLikesModal(key);
                          }
                        }}
                      />

                      {/* Reply button */}
                      {user && (
                        <button
                          onClick={() =>
                            setActiveReplyBox((prev) =>
                              prev === replyItem.id ? null : replyItem.id
                            )
                          }
                          style={{
                            fontSize: depth > 0 ? "12px" : "13px",
                            color: "#0077b6",
                            fontWeight: "bold",
                            background: "transparent",
                            border: "none",
                            padding: "4px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Reply
                        </button>
                      )}
                    </div>

                    {/* Reply box (for replying to this reply) */}
                    {activeReplyBox === replyItem.id && user && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <textarea
                              rows="2"
                              placeholder="Write a reply..."
                              value={nestedReplyText[replyItem.id] || ""}
                              onChange={(e) =>
                                setNestedReplyText((prev) => ({
                                  ...prev,
                                  [replyItem.id]: e.target.value,
                                }))
                              }
                              style={{
                                width: "100%",
                                borderRadius: "8px",
                                border: "1px solid #ddd",
                                padding: "6px",
                                fontSize: "13px",
                              }}
                            />
                            {/* Media input */}
                            <div
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                const files = Array.from(e.dataTransfer.files);
                                setNestedReplyMedia((prev) => ({
                                  ...prev,
                                  [replyItem.id]: [...(prev[replyItem.id] || []), ...files],
                                }));
                              }}
                              onClick={() => document.getElementById(`nested-upload-${replyItem.id}`).click()}
                              style={{
                                marginTop: "6px",
                                padding: "10px",
                                border: "2px dashed #0077b6",
                                borderRadius: "8px",
                                textAlign: "center",
                                cursor: "pointer",
                                color: "#0077b6",
                                fontSize: "14px",
                                position: "relative",
                              }}
                            >
                              Drag & Drop files here or click to select
                              <input
                                type="file"
                                id={`nested-upload-${replyItem.id}`}
                                multiple
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const files = Array.from(e.target.files);
                                  setNestedReplyMedia((prev) => ({
                                    ...prev,
                                    [replyItem.id]: [...(prev[replyItem.id] || []), ...files],
                                  }));
                                }}
                              />
                            </div>

                            {/* Preview selected files */}
                            <div style={{ display: "flex", flexWrap: "wrap", marginTop: "10px", gap: "10px" }}>
                              {(nestedReplyMedia[replyItem.id] || []).map((file, idx) => {
                                const url = URL.createObjectURL(file);
                                const isImageFile = file.type.startsWith("image/");
                                const isVideoFile = file.type.startsWith("video/");

                                return (
                                  <div key={idx} style={{ position: "relative" }}>
                                    {isImageFile && (
                                      <img
                                        src={url}
                                        alt="preview"
                                        style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "6px" }}
                                      />
                                    )}
                                    {isVideoFile && (
                                      <video
                                        src={url}
                                        style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "6px" }}
                                        controls
                                      />
                                    )}
                                    {/* Delete button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNestedReplyMedia((prev) => ({
                                          ...prev,
                                          [replyItem.id]: prev[replyItem.id].filter((_, i) => i !== idx),
                                        }));
                                      }}
                                      style={{
                                        position: "absolute",
                                        top: "-5px",
                                        right: "-5px",
                                        background: "#d9534f",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: "20px",
                                        height: "20px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ marginTop: "6px" }}>
                              <button
                                onClick={() => handleNestedReplySubmit(post.id, replyItem.id)}
                                style={{
                                  padding: "6px 12px",
                                  background: "none",
                                  color: "#0135edff",
                                  border: "1px solid #0135edff",
                                  borderRadius: "20px",
                                  cursor: "pointer",
                                  marginRight: "10px",
                                }}
                              >
                                Reply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show/hide nested replies button */}
                    {hasChildren && (
                      <div style={{ marginTop: "10px" }}>
                        <button
                          onClick={() =>
                            setShowNestedReplies((prev) => ({
                              ...prev,
                              [replyItem.id]: !prev[replyItem.id],
                            }))
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: "#0077b6",
                            cursor: "pointer",
                            fontSize: "13px",
                            padding: 0,
                          }}
                        >
                          {isExpanded
                            ? "Hide replies"
                            : `View ${childReplies.length} repl${
                                childReplies.length > 1 ? "ies" : "y"
                              }`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recursively render child replies */}
                {hasChildren && isExpanded && (
                  <div style={{ marginTop: "8px" }}>
                    {childReplies.map((childReply) => renderNestedReply(childReply, depth + 1))}
                  </div>
                )}
              </div>
            );
          };

          return (
            <motion.div
              key={reply.id || i}
              ref={
                i === ((visibleReplies[post.id] ?? 7) - 1)
                  ? scrollRef
                  : null
              }
              initial={{ opacity: 0, translateY: 10 }}
              animate={{ opacity: 1, translateY: 0 }}
              exit={{ opacity: 0, translateY: -10 }}
              transition={{ duration: 0.3 }}
              style={{
                borderTop: "1px solid #eee",
                paddingTop: "10px",
                marginTop: "10px",
              }}
            >
              {renderNestedReply(reply, 0)}
            </motion.div>
          );
        })}
    </AnimatePresence>
  </div>
)}
      </div>
    ))}

       {(totalPages > 1 || hasMorePages) && (
     <div
       style={{
         display: 'flex',
         justifyContent: 'center',
         marginTop: '20px',
         gap: '10px',
         flexWrap: 'wrap'
       }}
     >
       <button
         disabled={pageLoading || currentPage === 1}
         onClick={() => handlePageChange(currentPage - 1)}
         style={{
           background: 'transparent',
           border: '2px solid #4a90e2',
           color: '#4a90e2',
           padding: '8px 16px',
           borderRadius: '6px',
           fontSize: '14px',
           cursor: pageLoading || currentPage === 1 ? 'not-allowed' : 'pointer',
           opacity: pageLoading || currentPage === 1 ? 0.5 : 1,
           transition: 'all 0.3s ease'
         }}
         onMouseEnter={(e) => {
           if (!pageLoading && currentPage !== 1) {
             e.target.style.background = '#4a90e2';
             e.target.style.color = '#fff';
           }
         }}
         onMouseLeave={(e) => {
           if (!pageLoading && currentPage !== 1) {
             e.target.style.background = 'transparent';
             e.target.style.color = '#4a90e2';
           }
         }}
       >
         &laquo; Prev
       </button>
   
       <span style={{ alignSelf: 'center', fontSize: '14px', color: '#555' }}>
         Page {currentPage} of {Math.max(totalPages, currentPage)}
       </span>
   
       <button
         disabled={pageLoading || (currentPage >= totalPages && !hasMorePages)}
         onClick={() => handlePageChange(currentPage + 1)}
         style={{
           background: 'transparent',
           border: '2px solid #4a90e2',
           color: '#4a90e2',
           padding: '8px 16px',
           borderRadius: '6px',
           fontSize: '14px',
           cursor: pageLoading || (currentPage >= totalPages && !hasMorePages) ? 'not-allowed' : 'pointer',
           opacity: pageLoading || (currentPage >= totalPages && !hasMorePages) ? 0.5 : 1,
           transition: 'all 0.3s ease'
         }}
         onMouseEnter={(e) => {
           if (!pageLoading && (currentPage < totalPages || hasMorePages)) {
             e.target.style.background = '#4a90e2';
             e.target.style.color = '#fff';
           }
         }}
         onMouseLeave={(e) => {
           if (!pageLoading && (currentPage < totalPages || hasMorePages)) {
             e.target.style.background = 'transparent';
             e.target.style.color = '#4a90e2';
           }
         }}
       >
         Next &raquo;
       </button>
     </div>
   )}

{/* 🔹 Who Liked Panel (hover-to-show, positioned above buttons, not overlapping media) */}
{showLikesModal && hoveredLikePosition && (() => {
  const list = whoLiked[showLikesModal] || [];
  const visibleCount = 20;
  const hasMore = list.length > visibleCount;
  const visible = hasMore ? list.slice(0, visibleCount) : list;
  const remaining = list.length - visible.length;

  // Calculate safe position: above the button, within viewport
  const modalHeight = Math.min(260, list.length * 20 + 50);
  const topPosition = Math.max(10, hoveredLikePosition.top - modalHeight - 10); // Position above, with margin
  const leftPosition = Math.max(10, Math.min(hoveredLikePosition.left, window.innerWidth - 340));

  return (
    <div
      style={{
        position: 'fixed',
        top: `${topPosition}px`,
        left: `${leftPosition}px`,
        zIndex: 1200,
        pointerEvents: 'none',
      }}
      onMouseEnter={() => {
        // Keep modal open when hovering over it
      }}
      onMouseLeave={() => {
        setShowLikesModal(null);
        setHoveredLikePosition(null);
      }}
    >
      <div
        style={{
          backgroundColor: 'rgba(74, 76, 83, 0.98)',
          color: '#f9fafb',
          borderRadius: '10px',
          padding: '10px 14px',
          maxWidth: '320px',
          maxHeight: '260px',
          overflowY: 'auto',
          boxShadow: '0 18px 35px rgba(155, 162, 180, 0.55)',
          fontSize: '12px',
          position: 'relative',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '6px', opacity: 0.9, paddingRight: '14px' }}>Reactions</div>
        {list.length === 0 ? (
          <div style={{ padding: '8px 0', color: '#9ca3af', fontSize: '11px' }}>
            No reactions yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {visible.map((liker, idx) => (
              <div
                key={liker.user_id || idx}
                style={{
                  padding: '2px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <ReactionIcon type={liker.reaction_type || DEFAULT_REACTION} size={12} />
                <span style={{ fontWeight: 500 }}>{getLikerDisplayName(liker)}</span>
              </div>
            ))}
            {hasMore && (
              <div style={{ paddingTop: '4px', fontWeight: 500 }}>
                and {remaining} more...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
})()}
  </div>
);
}
export default PostListGuest;
